import { useSeoMeta } from '@unhead/react';
import { Link } from 'react-router-dom';
import { Activity, ArrowRight, BookOpen, FileText, Star, WifiOff } from 'lucide-react';
import { Layout } from '@/components/Layout';
import { ActivityFeed } from '@/components/ActivityFeed';
import { ArticleGrid } from '@/components/ArticleGrid';
import { PublicationCard } from '@/components/PublicationCard';
import { OfflineArticleCard } from '@/components/OfflineArticleCard';
import { OfflinePublicationCard } from '@/components/OfflinePublicationCard';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useRecentWikiArticles } from '@/hooks/useWikiArticles';
import { useRecentPublications } from '@/hooks/usePublications';
import { useTopStarredArticles } from '@/hooks/useTopStarredArticles';
import { useOfflineLibrary } from '@/hooks/useOfflineLibrary';
import { useOfflinePublications } from '@/hooks/useOfflinePublications';

/**
 * The homepage — deliberately a **working surface**, not a landing page.
 *
 * It previously led with a large marketing hero explaining what Wikistar
 * is, pushing the actual content well below the fold. That pitch now
 * lives on `/about` (see `AboutPage`), linked from here and the footer,
 * leaving this page to do one job: get a reader into an article as
 * quickly as possible — jump straight to a title, or browse recent /
 * most-starred / publications / their own offline library from one
 * tabbed panel without navigating away.
 */
const Index = () => {
  useSeoMeta({
    title: 'Wikistar — A decentralized wiki on Nostr',
    description:
      'Read and write encyclopedia articles that live on Nostr relays, not servers. Browse recent and most-starred articles, curated publications, and your offline library.',
  });

  const { data: articles, isLoading } = useRecentWikiArticles(24);
  const { data: publications, isLoading: publicationsLoading } = useRecentPublications(12);
  const { data: starred, isLoading: starredLoading } = useTopStarredArticles(24);
  const { articles: offlineArticles, count: offlineArticleCount } = useOfflineLibrary();
  const { publications: offlinePublications, count: offlinePublicationCount } = useOfflinePublications();

  const offlineTotal = offlineArticleCount + offlinePublicationCount;

  return (
    <Layout>
      <div className="mx-auto max-w-5xl px-4 py-8">

        {/* One tabbed panel for every way of finding something to read,
            so switching between them costs no page navigation. */}
        <Tabs defaultValue="recent">
          <TabsList className="flex h-auto w-full flex-wrap justify-start">
            <TabsTrigger value="recent" className="gap-1.5">
              <FileText className="size-4" />
              Recent
            </TabsTrigger>
            <TabsTrigger value="activity" className="gap-1.5">
              <Activity className="size-4" />
              Activity
            </TabsTrigger>
            <TabsTrigger value="starred" className="gap-1.5">
              <Star className="size-4" />
              Most starred
            </TabsTrigger>
            <TabsTrigger value="publications" className="gap-1.5">
              <BookOpen className="size-4" />
              Publications
            </TabsTrigger>
            <TabsTrigger value="offline" className="gap-1.5">
              <WifiOff className="size-4" />
              Offline
              {offlineTotal > 0 && <span className="text-muted-foreground">({offlineTotal})</span>}
            </TabsTrigger>
          </TabsList>

          <TabsContent value="recent" className="mt-6">
            <ArticleGrid
              articles={articles}
              isLoading={isLoading}
              emptyMessage="No articles found yet. Try checking your relay connections, or be the first to write one!"
            />
            {/* The tab is a capped preview — the full, paginated list lives
                on /articles. Only offer the link when the preview filled
                up, i.e. there is (almost certainly) more to see. */}
            {articles && articles.length >= 24 && (
              <div className="mt-4 flex justify-end">
                <Button variant="ghost" size="sm" asChild>
                  <Link to="/articles">
                    All articles
                    <ArrowRight className="size-4" />
                  </Link>
                </Button>
              </div>
            )}
          </TabsContent>

          <TabsContent value="activity" className="mt-6">
            <ActivityFeed maxItems={15} />
          </TabsContent>

          <TabsContent value="starred" className="mt-6">
            <div className="mb-4 flex justify-end">
              <Button variant="ghost" size="sm" asChild>
                <Link to="/stars">
                  Full leaderboard
                  <ArrowRight className="size-4" />
                </Link>
              </Button>
            </div>
            <ArticleGrid
              articles={starred?.map(({ article }) => article)}
              isLoading={starredLoading}
              emptyMessage="No starred articles yet. Star one you love to help others find it."
            />
          </TabsContent>

          <TabsContent value="publications" className="mt-6">
            <div className="mt-4">
              {publicationsLoading ? (
              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {Array.from({ length: 3 }).map((_, i) => (
                  <Skeleton key={i} className="h-48 w-full" />
                ))}
              </div>
            ) : publications && publications.length > 0 ? (
              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {publications.map((event) => (
                  <PublicationCard key={event.id} event={event} />
                ))}
              </div>
            ) : (
              <Card className="border-dashed">
                <CardContent className="py-12 px-8 text-center">
                  <p className="mx-auto max-w-sm text-muted-foreground">
                    No publications found yet. Create a multi-section book, guide, or documentation set.
                  </p>
                </CardContent>
              </Card>
            )}
            </div>
          </TabsContent>

          <TabsContent value="offline" className="mt-6">
            {offlineTotal === 0 ? (
              <Card className="border-dashed">
                <CardContent className="py-12 px-8 text-center">
                  <p className="mx-auto max-w-md text-muted-foreground">
                    Nothing saved yet. Use <span className="font-medium text-foreground">Save</span> on any article or
                    publication to keep a copy in this browser — readable with no connection, and a good match for{' '}
                    <span className="font-medium text-foreground">Reader</span> mode on an e-reader.
                  </p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-8">
                <p className="text-sm text-muted-foreground">
                  Saved in this browser and readable with no connection.
                </p>
                {offlinePublicationCount > 0 && (
                  <section>
                    <h3 className="mb-3 text-sm font-semibold text-muted-foreground">
                      Publications ({offlinePublicationCount})
                    </h3>
                    <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                      {offlinePublications.map((publication) => (
                        <OfflinePublicationCard key={publication.index.id} publication={publication} />
                      ))}
                    </div>
                  </section>
                )}
                {offlineArticleCount > 0 && (
                  <section>
                    <h3 className="mb-3 text-sm font-semibold text-muted-foreground">
                      Articles ({offlineArticleCount})
                    </h3>
                    <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                      {offlineArticles.map((article) => (
                        <OfflineArticleCard key={article.event.id} article={article} />
                      ))}
                    </div>
                  </section>
                )}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </Layout>
  );
};

export default Index;
